import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { titleMRClass } from 'src/components/common/common';

interface Props {
	title?: string;
	value: string;
	setValue: (value: string) => void;
	editable: boolean;
	titleless?: boolean;
	name?: string;
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	wrapperOption?: {
		wrapperStyle: string;
		titleStyle: string;
		inputStyle: string;
	};
	placeholder?: string;
}

/** "1430" -> "14:30" (? 0-23, ? 0-59? ??) */
const formatTimeFromDigits = (digits: string): string => {
	if (!digits) return '00:00';
	const padded = digits.padStart(4, '0');
	let hours = parseInt(padded.slice(0, 2), 10);
	let minutes = parseInt(padded.slice(2, 4), 10);
	hours = Math.min(Math.max(hours, 0), 23);
	minutes = Math.min(Math.max(minutes, 0), 59);
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** "14:30" -> "1430" */
const toDigits = (value: string): string => value.replace(/\D/g, '');

export const TimeForm = (props: Props) => {
	const {
		title,
		value,
		setValue,
		editable,
		titleless = false,
		name,
		onChange,
		wrapperOption = {
			wrapperStyle: 'w-[300px] min-w-[300px] mr-[10px] mb-[5px]',
			titleStyle: 'w-[100px] min-w-[100px] text-left',
			inputStyle: 'w-[100px] min-w-[100px]',
		},
		placeholder = '?? ??',
	} = props;

	const inputRef = useRef<HTMLInputElement>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(toDigits(value));

	useEffect(() => {
		if (!isEditing) {
			setInputValue(toDigits(value));
		}
	}, [value, isEditing]);

	const handleDisplayClick = () => {
		if (!editable) return;
		setInputValue(toDigits(value));
		setIsEditing(true);
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const digits = e.target.value.replace(/\D/g, '');
		const next = digits.length > 4 ? digits.slice(-4) : digits;
		setInputValue(next);
		setTimeout(() => {
			inputRef.current?.setSelectionRange(next.length, next.length);
		}, 0);
	};

	const commitValue = () => {
		setIsEditing(false);
		const formatted = formatTimeFromDigits(inputValue);
		setValue(formatted);
		if (onChange) {
			onChange({ target: { name: name || '', value: formatted } } as ChangeEvent<HTMLInputElement>);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.currentTarget.blur();
		}
	};

	return (
		<div className={`flex h-[20px] ${wrapperOption.wrapperStyle}`}>
			<p
				className={`${wrapperOption.titleStyle} form-title ${titleMRClass} cursor-pointer ${titleless ? 'hidden' : ''}`}
				onClick={handleDisplayClick}
				tabIndex={editable ? 0 : -1}
			>
				{title}
			</p>
			{isEditing ? (
				<input
					ref={inputRef}
					className={`${editable ? 'bg-white' : 'bg-gray-disable'} px-[6px] text-left rounded-[4px] border-[1px] ${wrapperOption.inputStyle}`}
					value={inputValue}
					onChange={handleInputChange}
					onBlur={commitValue}
					onKeyDown={handleKeyDown}
					placeholder="1430"
					disabled={!editable}
					autoFocus
				/>
			) : (
				<p
					className={`${editable ? 'bg-white' : 'bg-gray-disable'} px-[6px] text-left rounded-[4px] border-[1px] cursor-pointer ${wrapperOption.inputStyle} overflow-hidden whitespace-nowrap`}
					onClick={handleDisplayClick}
					tabIndex={editable ? 0 : -1}
				>
					{value || placeholder}
				</p>
			)}
		</div>
	);
};
